<script setup lang="ts">
  import OrdersService from '@/api/services/orders.service';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { deviceCanEverPrint, usePrinting } from '@/composition/use-printing';
  import { useUi } from '@/composition/use-ui';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { PrinterType } from '@/modules/PrintService';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { AlertType } from '@/interfaces/IAlert';
  import { computed, inject, ref, Ref } from 'vue';
  import ModalAdminPin from '../../ModalAdminPin.vue';
  import { useAuth } from '@/composition/use-auth';
  import { PermissionType } from '@/enums/PermissionType';
  import { IAuthData } from '@/interfaces/IAuthData';
  import {
    getOnlineAcceptCajaGuardError,
    getPaidOrderCancelDeclineGuardError,
  } from '@/utils/orders.utils';

  const order = inject<Ref<IOrder>>('order')!;
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const modals = useModal();
  const printing = usePrinting();
  const auth = useAuth();
  const ordersHelper = useOrders();

  // Accept/decline require OrdersManage server-side: an OrdersHistoric-only
  // user (unified history route) would only get error toasts from them.
  // Cancelar stays: it is independently gated by the OrdersCancel admin pin.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const actionButtons = computed(() => {
    const o = order.value;

    const pendingStatuses = [
      OrderStatus.Received,
      OrderStatus.Progress,
      OrderStatus.ReadyToTake,
      OrderStatus.Delivering,
      OrderStatus.Finished,
    ];

    if (o.status === OrderStatus.Received) {
      if (!canManageOrders.value) return [];
      return [
        {
          label: 'Rechazar',
          color: 'red',
          icon: 'mdi-close',
          dark: true,
          oulined: true,
          click: () => {
            modals
              .confirm({
                title: 'Rechazar pedido',
                message: '¿Está seguro de que desea rechazar este pedido?',
              })
              .then((confirmed) => {
                if (!confirmed) return;

                setStatus(OrderStatus.Declined);
              });
          },
        },
        {
          label: 'Aceptar',
          color: 'green',
          icon: 'mdi-check',
          dark: true,
          oulined: false,
          click: () => setStatus(OrderStatus.Progress),
        },
      ];
    } else if (pendingStatuses.includes(o.status)) {
      return [
        {
          label: 'Cancelar',
          color: 'red',
          icon: 'mdi-delete',
          dark: true,
          oulined: true,
          click: async () => {
            /*modals.show(
              ModalAdminPin,
              {},
              {
                name: 'admin-pin',
                adaptive: true,
                classes: 'dialog--height-limit',
                height: 'auto',
                scrollable: true,
                maxWidth: window.innerWidth * 0.95,
                width: 640,
                transition: 'none',
              }
            );*/
            let authData: IAuthData | undefined;

            if (!auth.hasPermission(PermissionType.OrdersCancel)) {
              authData = await modals.showDynamicDialog<IAuthData>(
                ModalAdminPin,
                {
                  requiredPermission: PermissionType.OrdersCancel,
                  reason: 'order-cancel',
                },
                'admin-pin'
              );

              if (!authData) return;
            }

            modals
              .confirm({
                title: 'Cancelar pedido',
                message: '¿Está seguro de que desea cancelar este pedido?',
              })
              .then((confirmed) => {
                if (!confirmed) return;

                setStatus(OrderStatus.Canceled, authData);
              });
          },
        },
      ];
    }

    return [];
  });

  const updating = ref(false);
  async function setStatus(status: OrderStatus, authData?: IAuthData) {
    if (updating.value) return;

    try {
      const paidGuardError = getPaidOrderCancelDeclineGuardError({
        order: order.value,
        status,
      });
      if (paidGuardError) {
        ui.showToast(AlertType.Error, paidGuardError);
        return;
      }

      if (status === OrderStatus.Progress) {
        const guardError = getOnlineAcceptCajaGuardError({
          order: order.value,
          caja: restaurantStore.caja,
          effectiveTier: restaurantStore.effectiveTier,
          branchSettingUseCajas:
            restaurantStore.selectedBranch?.settingUseCajas,
        });

        if (guardError) {
          ui.showToast(AlertType.Error, guardError);
          return;
        }
      }

      updating.value = true;
      await OrdersService.setStatus(
        order.value!.id,
        status,
        restaurantStore.caja?.id,
        authData
      );
      // Orders outside the pending store set (unified history modal) get no
      // WS/store push: refresh the injected instance so the modal reflects
      // the new status (refreshOrder's Object.assign fallback covers them).
      // Best-effort: the status change already succeeded — a transient
      // refresh failure must not surface as an update error.
      try {
        await ordersHelper.refreshOrder({ order: order.value! });
      } catch (refreshError) {
        console.error(
          'Error refreshing order after status change',
          refreshError
        );
      }
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el estado');
    } finally {
      updating.value = false;
    }
  }

  // Ticket, Comanda and Etiquetas
  const printButtons = [
    {
      title: 'Ticket no fiscal',
      icon: 'mdi-ticket-percent',
      click: () => {
        printing
          .printOrder({
            printerType: PrinterType.Ticket,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir ticket');
          });
      },
    },
    {
      title: 'Comanda',
      icon: 'mdi-receipt',
      click: () => {
        printing
          .printOrder({
            printerType: PrinterType.Comanda,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir comanda');
          });
      },
    },
    {
      title: 'Etiquetas',
      icon: 'mdi-tag-text',
      click: () => {
        printing
          .printOrder({
            printerType: PrinterType.Etiqueta,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir etiquetas');
          });
      },
    },
    {
      title: 'Todo',
      icon: 'mdi-printer',
      click: () => {
        printing
          .printOrder({
            printerType: PrinterType.Ticket,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir ticket');
          });
        printing
          .printOrder({
            printerType: PrinterType.Comanda,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir comanda');
          });
        printing
          .printOrder({
            printerType: PrinterType.Etiqueta,
            order: order.value!,
          })
          .catch((error) => {
            ui.serverError(error, 'Error al imprimir etiquetas');
          });
      },
    },
  ];
</script>

<template>
  <div class="order-actions-container">
    <div class="order-actions">
      <v-menu v-if="deviceCanEverPrint" offset-y top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="action-button"
            color="grey"
            depressed
            dark
            v-on="on"
            v-bind="attrs"
          >
            <v-icon left>mdi-printer</v-icon>
            <template v-if="order.status !== OrderStatus.Received">
              Imprimir
            </template>
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="button in printButtons"
            :key="button.title"
            @click="button.click"
          >
            <v-list-item-icon>
              <v-icon>{{ button.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ button.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-for="button in actionButtons"
        :key="button.label"
        class="action-button"
        :color="button.color"
        depressed
        :outlined="button.oulined"
        :dark="button.dark"
        :loading="updating"
        @click="button.click"
      >
        <v-icon left>{{ button.icon }}</v-icon>
        {{ button.label }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .order-actions-container {
    @apply tw-border-t-2 tw-border-gray-200 tw-border-dashed tw-py-2;

    .order-actions {
      @apply tw-flex tw-space-x-2 tw-justify-between;

      .action-button {
        @apply tw-rounded-lg;
      }
    }
  }
</style>
